import React, { useEffect, useState, useCallback } from "react";
import "../../styles/quiz/RealFake.css";
import { useNavigate } from "react-router-dom";
import { fetchBooleanQuizzes } from "../../api/quizBooleanApi";
import RealFakeArticle from "../../components/quiz/RealFakeArticle";
import { PieChart, Pie, Cell } from "recharts";

// Main quiz component for Real or Fake questions
const QuizPage = () => {
  // State variables
  const [quizzes, setQuizzes] = useState([]); // Loaded quiz data
  const [currentIndex, setCurrentIndex] = useState(0); // Current question index
  const [showResult, setShowResult] = useState(false); // Whether to show result screen
  const [userAnswers, setUserAnswers] = useState([]); // User's answers
  const [showTip, setShowTip] = useState(false); // Toggle for hint/tip display
  const [selectedDetail, setSelectedDetail] = useState(null); // Expanded feedback detail
  const [warning, setWarning] = useState(""); // Warning if user submits early
  const navigate = useNavigate();

  // Load quiz data on mount
  useEffect(() => {
    fetchBooleanQuizzes().then(setQuizzes);
  }, []);

  // Handle user's answer and update progress
  const handleAnswer = useCallback(
    (userAnswer) => {
      const currentQuiz = quizzes[currentIndex];
      const isCorrect = userAnswer === currentQuiz.correct_answer;
      const updatedAnswers = [...userAnswers];
      updatedAnswers[currentIndex] = { ...currentQuiz, userAnswer, isCorrect };
      setUserAnswers(updatedAnswers);
      setShowTip(false);
      setWarning("");

      // Move to next question automatically
      if (currentIndex < quizzes.length - 1) {
        setTimeout(() => setCurrentIndex((prev) => prev + 1), 200);
      }
    },
    [quizzes, currentIndex, userAnswers]
  );

  // Navigate to previous/next question manually
  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
    setShowTip(false);
  };
  const handleNext = () => {
    if (currentIndex < quizzes.length - 1) setCurrentIndex((prev) => prev + 1);
    setShowTip(false);
  };

  // Navigate by clicking thumbnails
  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
    setShowTip(false);
  };

  // Handle submission and show results
  const handleFinish = () => {
    if (
      userAnswers.length !== quizzes.length ||
      userAnswers.includes(undefined)
    ) {
      setWarning("Please answer all questions before finishing the quiz.");
    } else {
      setShowResult(true);
      setWarning("");
    }
  };

  // Reset the quiz to try again
  const resetQuiz = () => {
    setCurrentIndex(0);
    setShowResult(false);
    setUserAnswers([]);
    setShowTip(false);
    setSelectedDetail(null);
    setWarning("");
  };

  // Show loading spinner while quizzes load
  if (quizzes.length === 0) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        Loading quiz...
      </div>
    );
  }

  // Score calculation
  const quiz = quizzes[currentIndex];
  const correctCount = userAnswers.filter((a) => a?.isCorrect).length;
  const wrongCount = quizzes.length - correctCount;
  const percent = (correctCount / quizzes.length) * 100;

  // Pie chart data
  const pieData = [
    { name: "Correct", value: correctCount },
    { name: "Wrong", value: wrongCount },
  ];
  const COLORS = ["#fbc640", "#5f6f56"];

  // Score-based feedback message
  const getFeedbackMessage = (score) => {
    if (score >= 9) {
      return {
        tier: "🎉 Expert Voter",
        message:
          "Amazing! You’re an expert at separating fact from fiction. Keep up your critical thinking!",
      };
    } else if (score >= 6) {
      return {
        tier: "👍 Informed Voter",
        message:
          "Well done! You're well-informed, with just a few tricky claims. Stay sharp and keep questioning.",
      };
    } else if (score >= 3) {
      return {
        tier: "🤔 Learning Voter",
        message:
          "You're on your way! Keep practicing your fact-checking skills and review the explanations carefully.",
      };
    } else {
      return {
        tier: "⚠️ Misinformed Risk",
        message:
          "It’s a tough start, but don't worry! Strengthen your media literacy by reviewing the explanations and trying again.",
      };
    }
  };

  return (
    <div className="quiz-wrapper text-center">
      {!showResult ? (
        <>
          {/* Quiz in progress */}
          <div className="quiz-layout">
            <h1 className="quiz-title mb-2">
              Can you spot what's real and what's fake?
            </h1>
            <p className="quiz-subtitle mb-5">
              🔎 Practice your fact-checking skills by verifying claims with
              official sources like Parliament records and government websites.
              Always review full contexts, official votes, or policy
              announcements.
            </p>

            {/* Thumbnail navigation */}
            <div className="thumbnail-navigation-wrapper">
              <div className="thumbnail-navigation">
                {quizzes.map((q, index) => (
                  <div
                    key={index}
                    className={`thumbnail-nav-item ${
                      index === currentIndex ? "active" : ""
                    }`}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <div className="quiz-thumbnail-number">#{index + 1}</div>
                    <img
                      src={q.image_url}
                      alt={q.headline}
                      className="thumbnail-img-small"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            <div className="quiz-content">
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${((currentIndex + 1) / quizzes.length) * 100}%`,
                  }}
                ></div>
              </div>

              {/* Main quiz article and navigation arrows */}
              <div className="quiz-article-navigation">
                <button onClick={handlePrev} className="arrow-button">
                  ◀
                </button>
                <RealFakeArticle
                  quiz={quiz}
                  showTip={showTip}
                  setShowTip={setShowTip}
                />
                <button onClick={handleNext} className="arrow-button">
                  ▶
                </button>
              </div>

              {/* Answer buttons */}
              <div className="answer-buttons mt-4">
                <button
                  className={`btn real-button ${
                    userAnswers[currentIndex]?.userAnswer === 1
                      ? "selected-answer"
                      : ""
                  }`}
                  onClick={() => handleAnswer(1)}
                >
                  REAL
                </button>
                <button
                  className={`btn fake-button ${
                    userAnswers[currentIndex]?.userAnswer === 0
                      ? "selected-answer"
                      : ""
                  }`}
                  onClick={() => handleAnswer(0)}
                >
                  FAKE
                </button>
              </div>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="mt-5 button-row">
            <button
              className="btn btn-warning"
              onClick={() => navigate("/challenge")}
            >
              Back to Challenges
            </button>
            <button className="btn btn-warning" onClick={handleFinish}>
              Submit Quiz
            </button>
          </div>

          {/* Warning message */}
          {warning && <p className="warning-text mt-2">{warning}</p>}
        </>
      ) : (
        <>
          {/* Result screen */}
          <h4 className="quiz-performance-heading">
            REAL OR FAKE QUIZ PERFORMANCE
          </h4>

          <div className="performance-summary-box">
            <div className="performance-text">
              {(() => {
                const { tier, message } = getFeedbackMessage(correctCount);
                return (
                  <>
                    <h5>{tier}</h5>
                    <p>{message}</p>
                  </>
                );
              })()}
              <p>
                Score {correctCount}/{quizzes.length}
              </p>
              {/* <p>Digital Citizenship Points: +{correctCount * 10}</p> */}
            </div>

            {/* Pie chart of correct vs. wrong */}
            <div className="pie-chart-container">
              <PieChart width={160} height={160}>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
              <div className="pie-center-label">{Math.round(percent)}%</div>
            </div>
          </div>

          {/* List of each question + user's answer + feedback */}
          <div className="answer-bar-list">
            {userAnswers.map((ans, i) => (
              <React.Fragment key={i}>
                <div
                  className="answer-bar-item"
                  onClick={() =>
                    setSelectedDetail(selectedDetail === i ? null : i)
                  }
                >
                  <div className="thumbnail-wrapper">
                    <img
                      src={ans.image_url}
                      alt={ans.headline}
                      className="thumbnail-img-small"
                    />
                    <div
                      className={`badge-overlay ${
                        ans.correct_answer === 1 ? "badge-real" : "badge-fake"
                      }`}
                    >
                      {ans.correct_answer === 1 ? "REAL" : "FAKE"}
                    </div>
                  </div>
                  <div className="bar-item-text">
                    {ans.headline}
                    <div
                      className={`result-status ${
                        ans.isCorrect ? "correct" : "wrong"
                      }`}
                    >
                      Your answer was {ans.isCorrect ? "Correct" : "Wrong"}
                    </div>
                    <div className="feedback-text-bar">📚 {ans.feedback}</div>
                  </div>
                </div>

                {/* Expanded feedback detail when clicked */}
                {selectedDetail === i && (
                  <div className="quiz-box newspaper-style-box mt-3">
                    <div
                      className="news-header"
                      style={{
                        color: ans.correct_answer === 1 ? "#2f8246" : "#c73232",
                      }}
                    >
                      {ans.correct_answer === 1 ? "REAL NEWS" : "FAKE NEWS"}
                    </div>
                    <hr className="news-line" />
                    <h2 className="headline">{ans.headline}</h2>
                    <hr className="news-line" />
                    <div className="news-inner-layout">
                      <div className="news-text">
                        <p className="summary">{ans.summary}</p>
                        <div className="tip-row">
                          <span
                            className="tip-toggle"
                            onClick={() => setShowTip((prev) => !prev)}
                          >
                            💡
                          </span>
                          {showTip && (
                            <span className="inline-tip">{ans.tip}</span>
                          )}
                        </div>
                      </div>
                      <div className="news-image">
                        <img
                          src={ans.image_url}
                          alt="quiz"
                          className="quiz-image-remote"
                        />
                        <div className="photo-credit">
                          Photo by Wikimedia Commons
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Buttons below result screen */}
          <div className="mt-4">
            <button className="btn btn-warning me-3" onClick={resetQuiz}>
              TRY AGAIN
            </button>
            <button
              className="btn btn-warning"
              onClick={() => navigate("/challenge")}
            >
              BACK TO CHALLENGES
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuizPage;
